import { clsx } from 'clsx';
import { useState } from 'react';
import { SideNavigationMenu, Button, TSideNavigationSection, ISideNavigationItem } from '@treezor/design-system';

import { SidebarHeader } from '@/router/components/sidebar/SidebarHeader';
import { SidebarFooter } from '@/router/components/sidebar/SidebarFooter';
import { SidebarLink } from '@/router/components/sidebar/SidebarLink';

type TSidebarProps = {
  isOpen: boolean;
  sections: TSideNavigationSection[];
  toggleSidebar: () => void;
  findSidebarItem?: (href: string) => ISideNavigationItem;
};

export function Sidebar({ sections, isOpen, toggleSidebar, findSidebarItem }: TSidebarProps) {
  const [isHover, setIsHover] = useState(false);
  const shouldShowSidebar = isOpen || (!isOpen && isHover);

  return (
    <nav
      className={clsx(
        'h-screen z-[1001] bg-icon-primary transition-[width] duration-200 ease-in top-0 left-0',
        isOpen ? 'relative' : 'fixed',
        shouldShowSidebar ? 'w-[256px]' : 'w-[72px]',
      )}
      onMouseEnter={() => !isOpen && setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <div className="overflow-hidden h-full">
        <div className="flex flex-col h-full">
          <SidebarHeader isOpen={shouldShowSidebar} />

          <div className="flex-1 overflow-x-hidden overflow-y-auto px-5">
            <SideNavigationMenu
              sections={sections.filter((section) => section.items.length > 0)}
              isNavOpen={shouldShowSidebar}
              navItemRenderer={({ href, children, classNames }) => {
                const item = findSidebarItem?.(href);
                return (
                  <SidebarLink id={item?.id} to={href} classNames={classNames}>
                    {children}
                  </SidebarLink>
                );
              }}
            />
          </div>

          <div className="p-5 transition-opacity duration-300 w-[256px] opacity-70 text-onColor typo-body-regular-caption">
            <SidebarFooter isOpen={shouldShowSidebar} />
          </div>
        </div>
      </div>

      <div className="bg-icon-primary absolute top-6 -right-3 rounded-full inline-flex">
        <Button
          variant="ghost"
          color="inverse"
          size="m"
          onClick={toggleSidebar}
          icon={isOpen ? 'chevron_left' : 'chevron_right'}
          isActive
        />
      </div>
    </nav>
  );
}
